import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, FormControl, InputLabel, Select, MenuItem,
  Box, Card, CardContent, Chip, CircularProgress, IconButton, Tooltip,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import CallMissedIcon from "@mui/icons-material/CallMissed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

const getStatusStyle = (status) => {
  const map = {
    completed: { bg: "rgba(16,185,129,0.08)", color: "#059669", label: "Completed" },
    busy: { bg: "rgba(239,68,68,0.08)", color: "#dc2626", label: "Busy" },
    "no-answer": { bg: "rgba(245,158,11,0.08)", color: "#d97706", label: "No Answer" },
    failed: { bg: "rgba(239,68,68,0.08)", color: "#dc2626", label: "Failed" },
    "in-progress": { bg: "rgba(59,130,246,0.08)", color: "#2563eb", label: "In Progress" },
    ringing: { bg: "rgba(59,130,246,0.08)", color: "#2563eb", label: "Ringing" },
    queued: { bg: "rgba(59,130,246,0.08)", color: "#2563eb", label: "Queued" },
  };
  return map[status] || { bg: "rgba(100,116,139,0.08)", color: "#64748b", label: status };
};

export default function CallLogs({ token }) {
  const authToken = token || localStorage.getItem("token");
  const [calls, setCalls] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchCalls = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/calls`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      setCalls(Array.isArray(data) ? data : []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching calls", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCalls();
    const id = setInterval(fetchCalls, 10000);
    return () => clearInterval(id);
  }, [authToken]);

  const handleRefresh = () => { setRefreshing(true); fetchCalls(); };

  const filteredCalls = calls.filter((c) => {
    if (filter === "all") return true;
    if (filter === "accepted") return c.status === "completed";
    if (filter === "in-progress") return ["queued", "ringing", "in-progress"].includes(c.status);
    return c.status === filter;
  });

  const stats = {
    total: calls.length,
    completed: calls.filter((c) => c.status === "completed").length,
    inProgress: calls.filter((c) => ["queued", "ringing", "in-progress"].includes(c.status)).length,
    failed: calls.filter((c) => ["busy", "no-answer", "failed"].includes(c.status)).length,
  };

  const statCards = [
    { label: "Total Calls", value: stats.total, color: "#6366f1", bg: "rgba(99,102,241,0.06)", border: "rgba(99,102,241,0.15)" },
    { label: "Completed", value: stats.completed, color: "#059669", bg: "rgba(16,185,129,0.06)", border: "rgba(16,185,129,0.15)" },
    { label: "In Progress", value: stats.inProgress, color: "#2563eb", bg: "rgba(59,130,246,0.06)", border: "rgba(59,130,246,0.15)" },
    { label: "Failed/Busy", value: stats.failed, color: "#dc2626", bg: "rgba(239,68,68,0.06)", border: "rgba(239,68,68,0.15)" },
  ];

  return (
    <div className="page-wrapper animate-in">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: "#0f172a", mb: 0.5 }}>Call Logs</Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
            Last updated: {lastUpdate.toLocaleTimeString()} · Auto-refresh every 10s
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ background: "#f1f5f9", "&:hover": { background: "#e2e8f0" } }}
          >
            <RefreshIcon sx={{ fontSize: 20, animation: refreshing ? "spin 1s linear infinite" : "none", "@keyframes spin": { "100%": { transform: "rotate(360deg)" } } }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 2, mb: 3 }}>
        {statCards.map((s) => (
          <Card key={s.label} sx={{ background: s.bg, border: `1px solid ${s.border}`, boxShadow: "none" }}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 500, mb: 0.5 }}>{s.label}</Typography>
              <Typography sx={{ color: s.color, fontWeight: 700, fontSize: "1.5rem" }}>{s.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Filter + Table */}
      <Box className="enterprise-card" sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <FilterListIcon sx={{ color: "#64748b", fontSize: 20 }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Status Filter">
              <MenuItem value="all">All Calls</MenuItem>
              <MenuItem value="accepted">Completed</MenuItem>
              <MenuItem value="busy">Busy</MenuItem>
              <MenuItem value="no-answer">No Answer</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
            </Select>
          </FormControl>
          <Typography sx={{ color: "#94a3b8", fontSize: "0.82rem", ml: "auto" }}>
            {filteredCalls.length} call{filteredCalls.length !== 1 ? "s" : ""} found
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#6366f1" }} />
          </Box>
        ) : filteredCalls.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CallMissedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
            <Typography sx={{ fontWeight: 600, color: "#64748b" }}>No calls found</Typography>
            <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>Try changing your filter or refresh</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ borderRadius: "10px", border: "1px solid #f1f5f9" }}>
            <Table>
              <TableHead sx={{ background: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>To</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>From</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Start Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCalls.map((call) => {
                  const s = getStatusStyle(call.status);
                  return (
                    <TableRow key={call.sid} sx={{ "&:hover": { background: "#fafafa" } }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PhoneIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                          <span>{call.to}</span>
                        </Box>
                      </TableCell>
                      <TableCell>{call.from}</TableCell>
                      <TableCell>
                        <Chip label={s.label} size="small" sx={{ background: s.bg, color: s.color, border: `1px solid ${s.color}20` }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {call.duration ? `${call.duration}s` : "—"}
                      </TableCell>
                      <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                        {call.startTime ? new Date(call.startTime).toLocaleString() : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </div>
  );
}
